import { useEffect, useRef, useState } from "react";
import {
  PlanTemplate,
  ProgramTemplate,
  type PlanTemplate as PlanTemplateT,
  type ProgramTemplate as ProgramTemplateT,
} from "@jackedlogs/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";

// Shared JL-official templates fetch — the first shared home for the
// /api/templates data path (lifted from mobile's TemplatesShelf, which
// fetched one kind per instance; the Library browser needs both). Same
// posture as the shelf: defensive per-item parse (drop bad rows, warn),
// fetch once per mount — the browser mounts when the Create-hub opens,
// so this IS the lazy load. apiFetch via ref, same convention as
// usePrograms (a fresh literal per render must not re-kick the fetch).

export type TemplatesStatus = "loading" | "ready" | "error";

const parseItems = <T>(
  data: unknown,
  schema: { safeParse: (raw: unknown) => { success: boolean; data?: T } },
  label: string,
): T[] => {
  const arr =
    data && typeof data === "object" && "items" in data
      ? (data as { items: unknown }).items
      : [];
  const items: T[] = [];
  if (Array.isArray(arr)) {
    for (const raw of arr) {
      const parsed = schema.safeParse(raw);
      if (parsed.success && parsed.data !== undefined) items.push(parsed.data);
      else console.warn(`[templates] ${label}_invalid`);
    }
  }
  return items;
};

export function useTemplates(opts: { apiFetch: ProgramApiFetch }) {
  const [planTemplates, setPlanTemplates] = useState<PlanTemplateT[]>([]);
  const [programTemplates, setProgramTemplates] = useState<
    ProgramTemplateT[]
  >([]);
  const [status, setStatus] = useState<TemplatesStatus>("loading");

  const apiRef = useRef(opts.apiFetch);
  useEffect(() => {
    apiRef.current = opts.apiFetch;
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [plansRaw, programsRaw] = await Promise.all([
          apiRef.current<unknown>("/api/templates/plans"),
          apiRef.current<unknown>("/api/templates/programs"),
        ]);
        if (cancelled) return;
        setPlanTemplates(parseItems(plansRaw, PlanTemplate, "plan"));
        setProgramTemplates(
          parseItems(programsRaw, ProgramTemplate, "program"),
        );
        setStatus("ready");
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { planTemplates, programTemplates, status };
}
